import type { ProfileLoadFailure, SignInDiagnostics, User } from './authService';

/**
 * Which of sign-in's two stages failed:
 * - `firebase_token`: Firebase gave no usable token (it threw, or nobody is signed in and
 *   there is no stored token), so nothing reached our database.
 * - `profile_query`: the token was fine; the `getUserProfile` request to Hasura failed.
 * - `unknown`: something else threw while building the user.
 */
export type SignInStage = 'firebase_token' | 'profile_query' | 'unknown';

/** One attempt, as the report lists them. */
export interface SignInAttempt {
  attempt: number;
  stage: SignInStage;
  /** The failure kind, or `threw` / `token_refresh_failed`. */
  outcome: string;
  gqlErrorCode?: string | null;
  httpStatus?: number;
  durationMs?: number;
}

/** The stage a failed attempt stopped in. A `FirebaseTokenError` is matched by name so this
 *  module stays free of the Firebase SDK. */
export function stageOf(failure: ProfileLoadFailure | undefined, error: unknown, diagnostics: SignInDiagnostics | undefined): SignInStage {
  if (error !== undefined) return (error as { name?: string })?.name === 'FirebaseTokenError' ? 'firebase_token' : 'unknown';
  if (failure?.kind === 'no_user_id' && diagnostics?.token.source === 'none') return 'firebase_token';
  return 'profile_query';
}

/**
 * The outcome of signing a Firebase user into the portal:
 * - `ready`: a permitted role and a loaded profile. The only outcome that enters the portal.
 * - `role_rejected`: the token's role may not use this app.
 * - `profile_missing`: a permitted role, but the profile did not load after the retries. The
 *   caller reports it and shows the "couldn't load your account" screen, because a user
 *   without a profile has no rooftop, no permissions and no products.
 */
export type SignInResolution =
  | { status: 'ready'; user: User; attempts: number }
  | { status: 'role_rejected'; user: User; diagnostics?: SignInDiagnostics }
  | {
      status: 'profile_missing';
      user: User | undefined;
      failure?: ProfileLoadFailure;
      error?: unknown;
      attempts: number;
      /** The stage the last attempt stopped in. */
      stage: SignInStage;
      /** The last attempt's token and query diagnostics. */
      diagnostics?: SignInDiagnostics;
      /** Every attempt, oldest first. */
      history: SignInAttempt[];
    };

export interface SignInDeps {
  build: () => Promise<{ user: User; profileFailure?: ProfileLoadFailure; diagnostics?: SignInDiagnostics }>;
  /** Forces a fresh ID token, so a retry reads the claims the server holds now. */
  refreshIdToken: () => Promise<void>;
  isPermittedRole: (user: User) => boolean;
  /** Waits between network retries. Injected so tests do not wait. */
  sleep?: (ms: number) => Promise<void>;
}

/** Waits before each retry of a failure that a slow or flaky connection explains. */
export const NETWORK_RETRY_DELAYS_MS = [1_000, 3_000, 6_000];

/** Hasura codes that mean the token itself was refused, which a fresh token can fix. */
const TOKEN_ERROR_CODES = new Set(['invalid-jwt', 'jwt-invalid-claims', 'invalid-headers', 'jwt-missing-role-claims']);

/**
 * What to do about a failed attempt:
 * - `network`: no answer, a timeout, a 5xx or a 429. Wait and try again, a few times.
 * - `token`: a refused token, no user-id claim, or no row. Force a fresh token and try once more.
 * - `stop`: an answer that will not change on retry (a permission or schema error, a 4xx).
 */
export function retryPolicy(failure: ProfileLoadFailure | undefined, threw: boolean): 'network' | 'token' | 'stop' {
  if (threw) return 'network';
  switch (failure?.kind) {
    case 'network_error':
      return 'network';
    case 'http_error':
      return failure.status >= 500 || failure.status === 429 ? 'network' : 'stop';
    case 'no_user_id':
    case 'not_found':
      return 'token';
    case 'graphql_error':
      return failure.code && TOKEN_ERROR_CODES.has(failure.code) ? 'token' : 'stop';
    default:
      return 'stop';
  }
}

const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Build the user and, while the role is permitted but the profile is missing, retry by the
 * failure's policy: network failures up to `NETWORK_RETRY_DELAYS_MS.length` times with those
 * waits, token failures once after forcing a fresh token, anything else not at all. Never
 * throws: a throw while reading the token or building the user counts as a network failure,
 * and if retries run out it is `profile_missing` with the error.
 */
export async function resolveSignInUser(deps: SignInDeps): Promise<SignInResolution> {
  const sleep = deps.sleep ?? defaultSleep;
  let attempts = 0;
  let networkRetries = 0;
  let tokenRefreshed = false;
  let last: { user: User; profileFailure?: ProfileLoadFailure; diagnostics?: SignInDiagnostics } | undefined;
  const history: SignInAttempt[] = [];
  // The most recent user any attempt built, for the report's claim names when the last
  // attempt threw before building one.
  let lastUser: User | undefined;

  for (;;) {
    let error: unknown;
    attempts += 1;
    last = undefined;
    try {
      last = await deps.build();
      lastUser = last.user;
      if (!deps.isPermittedRole(last.user)) return { status: 'role_rejected', user: last.user, ...(last.diagnostics ? { diagnostics: last.diagnostics } : {}) };
      if (last.user.profile) return { status: 'ready', user: last.user, attempts };
    } catch (err) {
      error = err;
    }

    const failure = error === undefined ? last?.profileFailure : undefined;
    const stage = stageOf(failure, error, last?.diagnostics);
    history.push({
      attempt: attempts,
      stage,
      outcome: error !== undefined ? 'threw' : (failure?.kind ?? 'unknown'),
      ...(failure?.kind === 'graphql_error' ? { gqlErrorCode: failure.code } : {}),
      ...(failure?.kind === 'http_error' ? { httpStatus: failure.status } : {}),
      ...(last?.diagnostics?.query ? { durationMs: last.diagnostics.query.durationMs } : {}),
    });

    const policy = retryPolicy(failure, error !== undefined);
    // The attempt that ended sign-in decides what is reported: a throw replaces, rather than
    // joins, a profile failure left over from an earlier attempt.
    const missing = {
      status: 'profile_missing' as const,
      user: last?.user ?? lastUser,
      attempts,
      stage,
      history,
      ...(last?.diagnostics ? { diagnostics: last.diagnostics } : {}),
      ...(error !== undefined ? { error } : { failure }),
    };

    if (policy === 'network' && networkRetries < NETWORK_RETRY_DELAYS_MS.length) {
      await sleep(NETWORK_RETRY_DELAYS_MS[networkRetries]);
      networkRetries += 1;
      continue;
    }
    if (policy === 'token' && !tokenRefreshed) {
      tokenRefreshed = true;
      try {
        await deps.refreshIdToken();
      } catch (err) {
        history.push({ attempt: attempts, stage: 'firebase_token', outcome: 'token_refresh_failed' });
        return { status: 'profile_missing', user: last?.user ?? lastUser, attempts, stage: 'firebase_token', history, error: err };
      }
      continue;
    }
    return missing;
  }
}
